import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  addAnnotation,
  loadDocument,
  removeAnnotation,
  setMark,
  setStatus,
  setVersion,
  updateAnnotation,
} from "../redux/osmSlice";
import { osmApi } from "../osmApi";
import { buildGradedPdf } from "../lib/exportPdf";
import { clampScale, round2, uid } from "../lib/annotations";
import AnnotationToolbar from "./AnnotationToolbar";
import HistoryDrawer from "./HistoryDrawer";
import PageThumbnails from "./PageThumbnails";
import PageViewer from "./PageViewer";
import QuestionIndex from "./QuestionIndex";
import ReopenDialog from "./ReopenDialog";
import SubmitDialog from "./SubmitDialog";
import SyncBadge from "./SyncBadge";
import ToolsPanel from "./ToolsPanel";

const TOOL_KEYS = {
  v: "select",
  h: "pan",
  p: "ink",
  i: "highlight",
  e: "eraser",
  r: "rect",
  o: "oval",
  a: "arrow",
  l: "line",
  t: "text",
  c: "tick",
  x: "cross",
  m: "mark",
  q: "qmark",
};

const TOOL_HINTS = {
  select:
    "Click to select. Drag to move. Drag the square handle to resize. Delete removes.",
  pan: "Drag to move around the page.",
  ink: "Draw on the page.",
  highlight: "Drag across the text to highlight it.",
  eraser: "Click or drag over an annotation to remove it.",
  rect: "Drag to draw a rectangle.",
  oval: "Drag to draw an oval.",
  arrow: "Drag to draw an arrow.",
  line: "Drag to draw a line.",
  text: "Click on the page and type. Enter saves, Shift+Enter adds a line.",
  tick: "Click to place a tick.",
  cross: "Click to place a cross.",
  mark: "Click to place the mark. It adds to the selected question.",
  qmark: "Click to tag the selected question on this page.",
  comment: "Click on the page to place the comment.",
  stamp: "Click on the page to place the stamp.",
};

const SHORTCUTS = [
  ["V / H", "Select / Pan"],
  ["P / I / E", "Pen / Highlighter / Eraser"],
  ["R / O / A / L", "Rectangle / Oval / Arrow / Line"],
  ["T", "Text"],
  ["C / X", "Tick / Cross"],
  ["M or 0–9", "Circle mark"],
  ["Q", "Question tag"],
  ["B", "Mark blank page and go next"],
  ["Space + drag", "Pan with any tool"],
  ["PgUp / PgDn", "Previous / next page"],
  ["Ctrl Z / Ctrl Y", "Undo / redo"],
  ["Ctrl + / Ctrl -", "Zoom"],
  ["Delete", "Remove selection"],
  ["Enter in a mark box", "Save and go to next question"],
  ["?", "Show this list"],
];

const VALID_SUM = /^\d+(\.\d+)?(\+\d+(\.\d+)?)+$/;

const cleanWork = (work = {}) =>
  Object.fromEntries(
    Object.entries(work)
      .map(([key, value]) => [
        key,
        String(value ?? "")
          .split("=")[0]
          .replace(/\s+/g, ""),
      ])
      .filter(([, value]) => VALID_SUM.test(value)),
  );

const draftKey = (scriptId) => `osm_draft_${scriptId}`;

const readDraft = (scriptId) => {
  try {
    return JSON.parse(localStorage.getItem(draftKey(scriptId)));
  } catch {
    return null;
  }
};

const clearDraft = (scriptId) => {
  try {
    localStorage.removeItem(draftKey(scriptId));
  } catch {
    // Nothing to clear if storage is blocked.
  }
};

const isTyping = (target) =>
  target?.tagName === "INPUT" ||
  target?.tagName === "TEXTAREA" ||
  target?.isContentEditable;

const flattenAnnotations = (annotations) =>
  Object.values(annotations || {}).flat();

function Pager({ page, total, onChange, view, onZoom }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        px: 0.5,
        py: 0.25,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: "0 4px 18px rgba(15,31,70,.18)",
      }}
    >
      <IconButton
        size="small"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mx: 1, whiteSpace: "nowrap" }}
      >
        {page} / {total}
      </Typography>
      <IconButton
        size="small"
        aria-label="Next page"
        disabled={page >= total}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRightIcon />
      </IconButton>
      {onZoom && (
        <>
          <IconButton
            size="small"
            aria-label="Zoom out"
            onClick={() => onZoom(-0.1)}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ width: 38, textAlign: "center" }}>
            {Math.round(view.scale * 100)}%
          </Typography>
          <IconButton
            size="small"
            aria-label="Zoom in"
            onClick={() => onZoom(0.1)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
}

export default function MarkingApp({
  scriptId,
  examiner,
  pdfDoc,
  originalBytes,
  paperScheme,
  scriptTitle,
  canReopen = true,
}) {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const osmState = useSelector((state) => state.osm);
  const showPanelByDefault = useMediaQuery("(min-width:1180px)");

  const [pageNo, setPageNo] = useState(1);
  const [view, setView] = useState({ scale: 1, fit: "width" });
  const [tool, setTool] = useState("select");
  const [options, setOptions] = useState({
    color: "#e53935",
    thickness: 2,
    textColor: "#e53935",
    fontSize: 14,
    bold: false,
    italic: false,
    underline: false,
    markValue: 1,
    commentText: "",
    stampText: "",
  });
  const [selectedId, setSelectedId] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [showPanel, setShowPanel] = useState(showPanelByDefault);
  const [viewedPages, setViewedPages] = useState(() => new Set([1]));
  const [history, setHistory] = useState({ past: [], future: [] });
  const [mode, setMode] = useState("answer");
  const [qpDoc, setQpDoc] = useState(null);
  const [qpPage, setQpPage] = useState(1);
  const [qpView, setQpView] = useState({ scale: 1, fit: "width" });
  const [qpLoading, setQpLoading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [draftOffer, setDraftOffer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const savedSnapshot = useRef(null);
  const versionRef = useRef(osmState.version);
  const inFlight = useRef(null);
  const saveTimer = useRef(null);
  const retryTimer = useRef(null);
  const retryDelay = useRef(5000);

  const getOsm = useCallback(() => store.getState().osm, [store]);
  const notify = useCallback((msg, severity = "info") => {
    setSnack({ open: true, msg, severity });
  }, []);

  const questions = useMemo(() => paperScheme?.questions || [], [paperScheme]);
  const annotations = useMemo(
    () => flattenAnnotations(osmState.annotations),
    [osmState.annotations],
  );
  const activeQuestion =
    questions.find((question) => question.key === activeKey) || null;
  const isLocked = osmState.status === "submitted";
  const hasConflict = osmState.status === "conflict";
  const totalPages = pdfDoc?.numPages || 0;

  const maxTotal = useMemo(
    () =>
      round2(
        questions.reduce(
          (sum, question) => sum + Number(question.maxMarks || 0),
          0,
        ),
      ),
    [questions],
  );
  const total = useMemo(
    () =>
      round2(
        questions.reduce(
          (sum, question) => sum + Number(osmState.marks?.[question.key] || 0),
          0,
        ),
      ),
    [questions, osmState.marks],
  );
  const markedCount = questions.filter(
    (question) =>
      osmState.marks?.[question.key] !== null &&
      osmState.marks?.[question.key] !== undefined,
  ).length;

  const pagesByQuestion = useMemo(() => {
    const pages = {};
    annotations.forEach((ann) => {
      if (!ann.qKey || !ann.page) return;
      pages[ann.qKey] = pages[ann.qKey] || new Set();
      pages[ann.qKey].add(ann.page);
    });
    return Object.fromEntries(
      Object.entries(pages).map(([key, set]) => [
        key,
        [...set].sort((a, b) => a - b),
      ]),
    );
  }, [annotations]);

  const annotationCounts = useMemo(() => {
    const counts = {};
    annotations.forEach((ann) => {
      counts[ann.page] = (counts[ann.page] || 0) + 1;
    });
    return counts;
  }, [annotations]);

  const unviewedPages = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, i) => i + 1).filter(
        (page) => !viewedPages.has(page),
      ),
    [totalPages, viewedPages],
  );

  const markingTheme = useCallback(
    (outer) =>
      createTheme(outer || {}, {
        // The workspace sits above the app shell, so popups must sit above the workspace.
        zIndex: { modal: 10000, snackbar: 10001, tooltip: 10002 },
        palette: {
          primary: { main: "#0a6cf0" },
          text: { primary: "#14213d", secondary: "#5b6982" },
        },
        shape: { borderRadius: 8 },
      }),
    [],
  );

  const hasUnsaved = useCallback(() => {
    const snapshot = savedSnapshot.current;
    if (!snapshot) return false;
    const current = getOsm();
    return ["marks", "choices", "work", "annotations"].some(
      (key) => snapshot[key] !== current[key],
    );
  }, [getOsm]);

  // Only one save runs at a time, so two PUTs can never race on the same version.
  // Edits made during a save go out straight after it.
  const flush = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    clearTimeout(retryTimer.current);

    const run = (async () => {
      if (!hasUnsaved()) return true;
      const status = getOsm().status;
      if (status === "conflict" || status === "submitted") return false;

      try {
        dispatch(setStatus("saving"));
        do {
          const sent = getOsm();
          // eslint-disable-next-line no-await-in-loop
          const response = await osmApi.putMarking(scriptId, {
            version: versionRef.current,
            examiner,
            marks: sent.marks,
            choices: sent.choices,
            work: cleanWork(sent.work),
            annotations: sent.annotations,
          });
          versionRef.current = response.version;
          savedSnapshot.current = sent;
          dispatch(setVersion(response.version));
        } while (hasUnsaved());

        retryDelay.current = 5000;
        clearDraft(scriptId);
        dispatch(setStatus("saved"));
        setLastSavedAt(new Date());
        return true;
      } catch (error) {
        const code = error.response?.status;

        if (code === 409) {
          dispatch(setStatus("conflict"));
          return false;
        }

        if (code >= 400 && code < 500) {
          dispatch(setStatus("error"));
          notify(
            error.response.data?.message || "The server rejected this save.",
            "error",
          );
          return false;
        }

        dispatch(setStatus(navigator.onLine ? "error" : "offline"));
        retryTimer.current = setTimeout(() => {
          retryDelay.current = Math.min(retryDelay.current * 2, 60000);
          flush();
        }, retryDelay.current);
        return false;
      }
    })().finally(() => {
      inFlight.current = null;
    });

    inFlight.current = run;
    return run;
  }, [dispatch, examiner, getOsm, hasUnsaved, notify, scriptId]);

  const loadScript = useCallback(async () => {
    const [doc, script] = await Promise.all([
      osmApi.getMarking(scriptId),
      osmApi.getScript(scriptId).catch(() => null),
    ]);

    const status = script?.status || doc.status;
    dispatch(loadDocument({ ...doc, status }));
    versionRef.current = getOsm().version;
    savedSnapshot.current = getOsm();

    const draft = readDraft(scriptId);
    if (draft?.version === doc.version && status !== "submitted")
      setDraftOffer(draft);
    else clearDraft(scriptId);
  }, [dispatch, getOsm, scriptId]);

  useEffect(() => {
    loadScript().catch((error) => {
      console.error(error);
      notify("Could not load saved marks.", "error");
    });
  }, [loadScript, notify]);

  useEffect(() => {
    if (!inFlight.current) versionRef.current = osmState.version;
  }, [osmState.version]);

  useEffect(() => {
    if (!hasUnsaved()) return undefined;

    const current = getOsm();
    try {
      localStorage.setItem(
        draftKey(scriptId),
        JSON.stringify({
          version: versionRef.current,
          marks: current.marks,
          choices: current.choices,
          work: current.work,
          annotations: current.annotations,
          savedAt: Date.now(),
        }),
      );
    } catch {
      // The server save still runs if local backup is full or blocked.
    }

    setDraftOffer(null);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 900);
    return () => clearTimeout(saveTimer.current);
  }, [
    osmState.marks,
    osmState.choices,
    osmState.work,
    osmState.annotations,
    flush,
    getOsm,
    hasUnsaved,
    scriptId,
  ]);

  useEffect(() => {
    const onOnline = () => flush();
    const onBeforeUnload = (event) => {
      if (!hasUnsaved()) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("beforeunload", onBeforeUnload);
      clearTimeout(saveTimer.current);
      clearTimeout(retryTimer.current);
    };
  }, [flush, hasUnsaved]);

  useEffect(() => {
    if (!activeKey && questions.length) setActiveKey(questions[0].key);
  }, [activeKey, questions]);

  const goToPage = useCallback(
    (page) => {
      const next = Math.min(Math.max(1, page), totalPages || 1);
      setPageNo(next);
      setSelectedId(null);
      setViewedPages((current) => new Set(current).add(next));
    },
    [totalPages],
  );

  const syncCircleMarks = useCallback(
    (qKey) => {
      if (!qKey) return;
      const stamps = flattenAnnotations(getOsm().annotations).filter(
        (ann) => ann.type === "mark" && ann.qKey === qKey,
      );
      const sum = round2(
        stamps.reduce((value, ann) => value + Number(ann.value || 0), 0),
      );
      dispatch(
        setMark({
          partId: qKey,
          mark: stamps.length ? sum : null,
          work:
            stamps.length > 1 ? stamps.map((ann) => ann.value).join("+") : "",
        }),
      );
    },
    [dispatch, getOsm],
  );

  const pushHistory = useCallback((entry) => {
    setHistory((current) => ({
      past: [...current.past.slice(-99), entry],
      future: [],
    }));
  }, []);

  const handleAdd = useCallback(
    (annotation) => {
      if (isLocked) return;

      const needsQuestion =
        annotation.type === "mark" || annotation.type === "qmark";
      if (needsQuestion && !activeQuestion) {
        notify("Select a question first.", "warning");
        return;
      }

      if (annotation.type === "mark") {
        const existing = flattenAnnotations(getOsm().annotations)
          .filter((ann) => ann.type === "mark" && ann.qKey === activeKey)
          .reduce((sum, ann) => sum + Number(ann.value || 0), 0);
        if (existing + Number(annotation.value) > activeQuestion.maxMarks) {
          notify(
            `Q${activeQuestion.no} cannot go above ${activeQuestion.maxMarks}.`,
            "warning",
          );
          return;
        }
      }

      const full = {
        ...annotation,
        id: uid(),
        page: pageNo,
        qKey: "qKey" in annotation ? annotation.qKey : activeKey,
        ...(annotation.type === "qmark" ? { qNo: activeQuestion.no } : {}),
      };
      dispatch(addAnnotation(full));
      pushHistory({ kind: "add", annotation: full });
      if (full.type === "mark") syncCircleMarks(full.qKey);
    },
    [
      activeKey,
      activeQuestion,
      dispatch,
      getOsm,
      isLocked,
      notify,
      pageNo,
      pushHistory,
      syncCircleMarks,
    ],
  );

  const handleUpdate = useCallback(
    (id, changes) => {
      const before = flattenAnnotations(getOsm().annotations).find(
        (ann) => ann.id === id,
      );
      if (!before) return;
      const previous = Object.fromEntries(
        Object.keys(changes).map((key) => [key, before[key]]),
      );
      dispatch(updateAnnotation({ id, changes }));
      pushHistory({ kind: "update", id, before: previous, after: changes });
    },
    [dispatch, getOsm, pushHistory],
  );

  const handleRemove = useCallback(
    (id) => {
      const annotation = flattenAnnotations(getOsm().annotations).find(
        (ann) => ann.id === id,
      );
      if (!annotation) return;
      dispatch(removeAnnotation(id));
      pushHistory({ kind: "remove", annotation });
      setSelectedId((current) => (current === id ? null : current));
      if (annotation.type === "mark") syncCircleMarks(annotation.qKey);
    },
    [dispatch, getOsm, pushHistory, syncCircleMarks],
  );

  const applyHistory = useCallback(
    (entry, direction) => {
      const insert =
        (entry.kind === "add" && direction === "redo") ||
        (entry.kind === "remove" && direction === "undo");
      const remove =
        (entry.kind === "add" && direction === "undo") ||
        (entry.kind === "remove" && direction === "redo");

      if (insert) dispatch(addAnnotation(entry.annotation));
      if (remove) dispatch(removeAnnotation(entry.annotation.id));
      if (entry.kind === "update") {
        dispatch(
          updateAnnotation({
            id: entry.id,
            changes: direction === "undo" ? entry.before : entry.after,
          }),
        );
      }
      if (entry.annotation?.type === "mark")
        syncCircleMarks(entry.annotation.qKey);
      if (entry.annotation?.page) goToPage(entry.annotation.page);
      setSelectedId(null);
    },
    [dispatch, goToPage, syncCircleMarks],
  );

  const undo = useCallback(() => {
    const entry = history.past[history.past.length - 1];
    if (!entry || isLocked) return;
    applyHistory(entry, "undo");
    setHistory({
      past: history.past.slice(0, -1),
      future: [...history.future, entry],
    });
  }, [applyHistory, history, isLocked]);

  const redo = useCallback(() => {
    const entry = history.future[history.future.length - 1];
    if (!entry || isLocked) return;
    applyHistory(entry, "redo");
    setHistory({
      past: [...history.past, entry],
      future: history.future.slice(0, -1),
    });
  }, [applyHistory, history, isLocked]);

  const zoom = useCallback((delta) => {
    setView((current) => ({
      scale: clampScale(current.scale + delta),
      fit: null,
    }));
  }, []);

  const qpZoom = useCallback((delta) => {
    setQpView((current) => ({
      scale: clampScale(current.scale + delta),
      fit: null,
    }));
  }, []);

  const chooseTool = useCallback((next) => {
    setTool(next);
    setSelectedId(null);
  }, []);

  const handleBlankPage = useCallback(() => {
    if (isLocked) return;
    handleAdd({
      type: "stamp",
      x: 0.4,
      y: 0.5,
      text: "Blank page",
      qKey: null,
    });
    goToPage(pageNo + 1);
  }, [goToPage, handleAdd, isLocked, pageNo]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isTyping(event.target) || submitOpen || reopenOpen) return;
      const key = event.key.toLowerCase();

      if (event.ctrlKey || event.metaKey) {
        if (key === "z") {
          event.preventDefault();
          if (event.shiftKey) redo();
          else undo();
        } else if (key === "y") {
          event.preventDefault();
          redo();
        } else if (key === "=" || key === "+") {
          event.preventDefault();
          zoom(0.1);
        } else if (key === "-") {
          event.preventDefault();
          zoom(-0.1);
        }
        return;
      }

      if (key === "b" && !isLocked) {
        handleBlankPage();
        return;
      }

      if (event.key === "?") setShortcutsOpen(true);
      else if (event.key === "PageDown") {
        event.preventDefault();
        goToPage(pageNo + 1);
      } else if (event.key === "PageUp") {
        event.preventDefault();
        goToPage(pageNo - 1);
      } else if (event.key === "Escape") {
        setSelectedId(null);
        setTool("select");
      } else if (
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedId &&
        !isLocked
      ) {
        event.preventDefault();
        handleRemove(selectedId);
      } else if (/^[0-9]$/.test(event.key) && !isLocked) {
        setOptions((current) => ({ ...current, markValue: Number(event.key) }));
        setTool("mark");
      } else if (TOOL_KEYS[key] && (!isLocked || key === "h")) {
        chooseTool(TOOL_KEYS[key]);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    chooseTool,
    goToPage,
    handleBlankPage,
    handleRemove,
    isLocked,
    pageNo,
    redo,
    reopenOpen,
    selectedId,
    submitOpen,
    undo,
    zoom,
  ]);

  const handleActivate = useCallback(
    (key, jump) => {
      setActiveKey(key);
      if (jump && pagesByQuestion[key]?.length)
        goToPage(pagesByQuestion[key][0]);
    },
    [goToPage, pagesByQuestion],
  );

  const handleQpUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setQpLoading(true);
    try {
      const data = await file.arrayBuffer();
      const doc = await pdfjsLib.getDocument({ data }).promise;
      setQpDoc(doc);
      setQpPage(1);
      setMode("split");
      notify("Question paper loaded.", "success");
    } catch (error) {
      console.error(error);
      notify("Could not open that PDF.", "error");
    } finally {
      setQpLoading(false);
    }
  };

  const handleBack = async () => {
    clearTimeout(saveTimer.current);
    await flush();
    navigate("/osm");
  };

  const handleReload = async () => {
    try {
      clearDraft(scriptId);
      await loadScript();
      dispatch(setStatus("saved"));
      setHistory({ past: [], future: [] });
      notify("Loaded the latest saved version.", "success");
    } catch (error) {
      console.error(error);
      notify("Could not reload this script.", "error");
    }
  };

  const restoreDraft = () => {
    dispatch(loadDocument({ ...draftOffer, status: getOsm().status }));
    setDraftOffer(null);
    notify("Your unsaved work was restored.", "success");
  };

  const discardDraft = () => {
    clearDraft(scriptId);
    setDraftOffer(null);
  };

  const viewCheckedCopy = async () => {
    const preview = window.open("", "_blank");

    try {
      const pdfBytes = await osmApi.getGradedPdfBytes(scriptId);

      const blob = new Blob([pdfBytes], {
        type: "application/pdf",
      });

      const objectUrl = URL.createObjectURL(blob);

      if (preview) {
        preview.location.href = objectUrl;
      } else {
        window.location.href = objectUrl;
      }

      // Revoke after the browser has had time to load the PDF.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      preview?.close();

      notify(
        error.response?.data?.message || "Could not open the checked copy.",
        "error",
      );
    }
  };

  const handleReopen = async (reason) => {
    setReopening(true);
    try {
      await osmApi.reopen(scriptId, reason);
      await loadScript();
      setReopenOpen(false);
      setHistory({ past: [], future: [] });
      notify("Script reopened.", "success");
    } catch (error) {
      notify(
        error.response?.data?.message || "Could not reopen this script.",
        "error",
      );
    } finally {
      setReopening(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      clearTimeout(saveTimer.current);
      const saved = await flush();
      if (!saved) {
        notify(
          "Marks are not saved yet, so the script was not submitted.",
          "error",
        );
        return;
      }

      const sourceBytes = originalBytes?.byteLength
        ? originalBytes
        : await pdfDoc.getData();

      const pdfBlob = await buildGradedPdf(
        sourceBytes,
        flattenAnnotations(getOsm().annotations),
      );

      await osmApi.submit(scriptId, versionRef.current, pdfBlob);
      dispatch(setStatus("submitted"));
      setSubmitOpen(false);
      notify("Script submitted.", "success");
    } catch (error) {
      console.error(error);
      notify(
        error.response?.data?.message || error.message || "Submit failed.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const hint = isLocked
    ? "This script is submitted and read-only."
    : mode === "qp"
      ? "Question paper view."
      : TOOL_HINTS[tool];

  const answerViewer = (
    <Box sx={{ position: "relative", flex: 1, minWidth: 0, height: "100%" }}>
      <PageViewer
        pdfDoc={pdfDoc}
        pageNo={pageNo}
        view={view}
        onViewChange={setView}
        annotations={annotations}
        tool={isLocked ? "pan" : tool}
        options={options}
        readOnly={isLocked}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onRemove={handleRemove}
      />
      <Pager page={pageNo} total={totalPages} onChange={goToPage} />
    </Box>
  );

  const paperViewer = (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: "100%",
        borderLeft: mode === "split" ? "2px solid #cfd9e8" : 0,
      }}
    >
      {qpDoc ? (
        <>
          <PageViewer
            pdfDoc={qpDoc}
            pageNo={qpPage}
            view={qpView}
            onViewChange={setQpView}
            tool="pan"
            readOnly
          />
          <Pager
            page={qpPage}
            total={qpDoc.numPages}
            onChange={(page) =>
              setQpPage(Math.min(Math.max(1, page), qpDoc.numPages))
            }
            view={qpView}
            onZoom={qpZoom}
          />
        </>
      ) : (
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          bgcolor="#e9eef6"
        >
          <Typography color="text.secondary">
            No question paper loaded.
          </Typography>
          <Button
            component="label"
            htmlFor="qp-upload"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={qpLoading}
          >
            {qpLoading ? "Loading…" : "Upload question paper"}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={markingTheme}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f4f7fb",
          color: "text.primary",
          overflow: "hidden",
        }}
      >
        <input
          id="qp-upload"
          type="file"
          accept="application/pdf"
          hidden
          onChange={handleQpUpload}
        />

        {/* Header */}
        <Box
          sx={{
            height: 56,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.5,
            bgcolor: "#fff",
            borderBottom: "1px solid #dfe7f3",
          }}
        >
          <Tooltip arrow title="Back to list">
            <IconButton
              size="small"
              aria-label="Back to list"
              onClick={handleBack}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {scriptTitle || "Answer Script Marking"}
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            aria-label="Document view"
            onChange={(_, value) => value && setMode(value)}
            sx={{
              ml: 1,
              "& .MuiToggleButton-root": { py: 0.4, textTransform: "none" },
            }}
          >
            <ToggleButton value="answer">Answer book</ToggleButton>
            <ToggleButton value="split">Side by side</ToggleButton>
            <ToggleButton value="qp">Question paper</ToggleButton>
          </ToggleButtonGroup>

          {qpDoc && (
            <Button
              component="label"
              htmlFor="qp-upload"
              size="small"
              sx={{ textTransform: "none" }}
            >
              Change paper
            </Button>
          )}

          <Box flex={1} />
          <SyncBadge lastSavedAt={lastSavedAt} onRetry={flush} />

          <Tooltip arrow title="History">
            <IconButton
              size="small"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <HistoryOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {isLocked ? (
            <>
              <Button
                variant="outlined"
                onClick={viewCheckedCopy}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                View checked copy
              </Button>
              {canReopen && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<LockOutlinedIcon />}
                  onClick={() => setReopenOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Reopen
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              disableElevation
              disabled={hasConflict}
              onClick={() => setSubmitOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Submit
            </Button>
          )}
        </Box>

        {hasConflict && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleReload}>
                Load latest
              </Button>
            }
          >
            This script was changed somewhere else. Load the latest version to
            continue. Changes not yet saved here will be lost.
          </Alert>
        )}

        {draftOffer && (
          <Alert
            severity="warning"
            action={
              <>
                <Button color="inherit" size="small" onClick={discardDraft}>
                  Discard
                </Button>
                <Button color="inherit" size="small" onClick={restoreDraft}>
                  Restore
                </Button>
              </>
            }
          >
            Unsaved work from {new Date(draftOffer.savedAt).toLocaleString()}{" "}
            was found on this device.
          </Alert>
        )}

        {mode !== "qp" && (
          <AnnotationToolbar
            tool={isLocked ? "pan" : tool}
            onTool={chooseTool}
            view={view}
            onZoom={zoom}
            onFit={(fit) => setView((current) => ({ ...current, fit }))}
            canUndo={history.past.length > 0}
            canRedo={history.future.length > 0}
            onUndo={undo}
            onRedo={redo}
            readOnly={isLocked}
            showPanel={showPanel}
            onTogglePanel={() => setShowPanel((current) => !current)}
          />
        )}

        <Box flex={1} minHeight={0} display="flex">
          {/* Questions */}
          <Box
            sx={{
              width: 212,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              borderRight: "1px solid #dfe7f3",
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: "1px solid #dfe7f3" }}>
              <Box
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                  {total}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {" "}
                    / {maxTotal}
                  </Typography>
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  questions.length ? (markedCount / questions.length) * 100 : 0
                }
                sx={{ mt: 1, height: 5, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {markedCount} of {questions.length} marked
              </Typography>
            </Box>
            <Box flex={1} overflow="auto">
              <QuestionIndex
                paper={paperScheme}
                marks={osmState.marks}
                work={osmState.work}
                activeKey={activeKey}
                pagesByQuestion={pagesByQuestion}
                readOnly={isLocked}
                onActivate={handleActivate}
                onJumpToPage={goToPage}
                onMark={(partId, value) => {
                  if (!isLocked) dispatch(setMark({ partId, ...value }));
                }}
              />
            </Box>
          </Box>

          {mode !== "qp" && (
            <PageThumbnails
              pdfDoc={pdfDoc}
              pageNo={pageNo}
              viewedPages={viewedPages}
              counts={annotationCounts}
              onSelect={goToPage}
            />
          )}

          <Box flex={1} minWidth={0} display="flex">
            {mode !== "qp" && answerViewer}
            {mode !== "answer" && paperViewer}
          </Box>

          {mode !== "qp" && showPanel && (
            <ToolsPanel
              tool={tool}
              options={options}
              activeQuestion={activeQuestion}
              readOnly={isLocked}
              onTool={chooseTool}
              onOptions={(patch) =>
                setOptions((current) => ({ ...current, ...patch }))
              }
            />
          )}
        </Box>

        {/* Status bar */}
        <Box
          sx={{
            height: 30,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            bgcolor: "#fff",
            borderTop: "1px solid #dfe7f3",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ flex: 1 }}
          >
            {hint}
          </Typography>
          {!isLocked && mode !== "qp" && (
            <Button
              size="small"
              onClick={handleBlankPage}
              sx={{ textTransform: "none", py: 0 }}
            >
              Blank page, next
            </Button>
          )}
          {activeQuestion && (
            <Typography variant="caption" fontWeight={700}>
              Marking Q{activeQuestion.no}
            </Typography>
          )}
          <Tooltip arrow title="Keyboard shortcuts (?)">
            <IconButton
              size="small"
              aria-label="Keyboard shortcuts"
              onClick={() => setShortcutsOpen(true)}
            >
              <KeyboardOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <HistoryDrawer
          open={historyOpen}
          scriptId={scriptId}
          onClose={() => setHistoryOpen(false)}
        />

        <ReopenDialog
          open={reopenOpen}
          busy={reopening}
          onClose={() => setReopenOpen(false)}
          onConfirm={handleReopen}
        />

        <SubmitDialog
          open={submitOpen}
          submitting={submitting}
          questions={questions}
          marks={osmState.marks || {}}
          total={total}
          maxTotal={maxTotal}
          unviewedPages={unviewedPages}
          onClose={() => setSubmitOpen(false)}
          onConfirm={handleSubmit}
          onJumpQuestion={(key) => {
            setSubmitOpen(false);
            handleActivate(key, true);
          }}
          onJumpPage={(page) => {
            setSubmitOpen(false);
            setMode((current) => (current === "qp" ? "answer" : current));
            goToPage(page);
          }}
        />

        <Dialog
          open={shortcutsOpen}
          maxWidth="xs"
          fullWidth
          onClose={() => setShortcutsOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Keyboard shortcuts</DialogTitle>
          <DialogContent dividers>
            {SHORTCUTS.map(([keys, action]) => (
              <Box
                key={keys}
                display="flex"
                justifyContent="space-between"
                gap={2}
                py={0.5}
              >
                <Typography variant="body2" fontWeight={700}>
                  {keys}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="right"
                >
                  {action}
                </Typography>
              </Box>
            ))}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setSnack((current) => ({ ...current, open: false }))}
        >
          <Alert severity={snack.severity} variant="filled">
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
